// The cost model (D109): every price and assumption behind what a plan costs us, each with its
// basis, source and date, editable with a reason (audited) and back to its default. Console D's
// Plan and usage (APS-412, admin-d.md): a strip, the price per thousand by profile, the unbilled
// units as `.kv`, each edit in its form window under its row.
import { KV, PageHead, Section, SectionHead, Strip } from '@apsio/ui'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import {
  type FormEvent,
  Fragment,
  useEffect,
  useId,
  useLayoutEffect,
  useRef,
  useState,
} from 'react'
import {
  type CostItem,
  type CostItemEdit,
  type CostModel,
  costModelQuery,
  setCostModelItem,
} from '../api/queries'
import { DataTable, FormWindow, parseUtc, ReasonField, UtcField } from '../components/kit'
import { ErrorState, Loading } from '../components/states'
import { type MessageId, useI18n } from '../i18n'
import { percent, useFormat } from '../lib/format'

const KINDS: { kind: CostItem['kind']; title: MessageId }[] = [
  { kind: 'fixed_monthly', title: 'costModel.kind.fixed_monthly' },
  { kind: 'per_unit', title: 'costModel.kind.per_unit' },
  { kind: 'rate', title: 'costModel.kind.rate' },
  { kind: 'assumption', title: 'costModel.kind.assumption' },
]

const BASIS: Record<CostItem['basis'], MessageId> = {
  fact: 'costModel.basis.fact',
  measured: 'costModel.basis.measured',
  estimate: 'costModel.basis.estimate',
}

const UNIT_NAME: Record<string, MessageId> = {
  usd_month: 'costModel.unitName.usd_month',
  usd_per_million: 'costModel.unitName.usd_per_million',
  usd_per_gb_month: 'costModel.unitName.usd_per_gb_month',
  usd_per_1k: 'costModel.unitName.usd_per_1k',
  brl: 'costModel.unitName.brl',
  brl_per_usd: 'costModel.unitName.brl_per_usd',
  ratio: 'costModel.unitName.ratio',
  count: 'costModel.unitName.count',
  bytes: 'costModel.unitName.bytes',
  seconds: 'costModel.unitName.seconds',
}

const COMPONENTS = ['workers', 'queues', 'kv', 'raw', 'ch_disk', 'replay', 'cpu'] as const

/** A value in its unit, as the owner reads it. */
function useValue() {
  const { t, number } = useI18n()
  const usd = (n: number, max = 4) =>
    number(n, { style: 'currency', currency: 'USD', maximumFractionDigits: max })
  return (x: Pick<CostItem, 'value' | 'unit'>): string => {
    switch (x.unit) {
      case 'usd_month':
        return `${usd(x.value, 2)} / ${t('costModel.month')}`
      case 'usd_per_million':
        return `${usd(x.value)} / M`
      case 'usd_per_gb_month':
        return `${usd(x.value)} / GB-mo`
      case 'usd_per_1k':
        return `${usd(x.value)} / 1k`
      case 'brl':
        return number(x.value, { style: 'currency', currency: 'BRL' })
      case 'brl_per_usd':
        return `${number(x.value, { style: 'currency', currency: 'BRL' })} / US$`
      case 'ratio':
        return percent(number, x.value, 2)
      case 'bytes':
        return `${number(x.value)} B`
      case 'seconds':
        return `${number(x.value)} s`
      default:
        return number(x.value)
    }
  }
}

/**
 * The edit window (kit/FormWindow), in an expanded row under the row it edits (focus moves into
 * it): the value, its basis, source and date, and the required reason. A custom value can go back
 * to the default with the same reason.
 */
function Edit({ item, onDone }: { item: CostItem; onDone: () => void }) {
  const { t } = useI18n()
  const value0 = useValue()
  const box = useRef<HTMLDivElement>(null)
  // The row is as wide as the table, which scrolls sideways on a phone: the window keeps to the
  // visible width of its scroll region (and sticks to its left edge, in CSS).
  useLayoutEffect(() => {
    const region = box.current?.closest<HTMLElement>('.scroll-x')
    if (!region || typeof ResizeObserver === 'undefined') return
    const size = () => {
      if (box.current) box.current.style.width = `${region.clientWidth}px`
    }
    size()
    const observer = new ResizeObserver(size)
    observer.observe(region)
    return () => observer.disconnect()
  }, [])
  useEffect(() => box.current?.querySelector<HTMLInputElement>('input')?.focus(), [])
  const client = useQueryClient()
  const ratio = item.unit === 'ratio'
  const [value, setValue] = useState(String(ratio ? +(item.value * 100).toFixed(4) : item.value))
  const [basis, setBasis] = useState(item.basis)
  const [source, setSource] = useState(item.source)
  const [asOf, setAsOf] = useState(item.as_of)
  const [note, setNote] = useState(item.note)
  const [reason, setReason] = useState('')
  const ids = { value: useId(), basis: useId(), source: useId(), note: useId() }
  const save = useMutation({
    mutationFn: (edit: CostItemEdit) => setCostModelItem({ key: item.key, edit }),
    onSuccess: async () => {
      await client.invalidateQueries({ queryKey: ['cost-model'] })
      onDone()
    },
  })
  const submit = (e: FormEvent) => {
    e.preventDefault()
    if (parseUtc(asOf, { time: false }) === null) return
    const n = Number(value.replace(',', '.'))
    save.mutate({ value: ratio ? n / 100 : n, basis, source, as_of: asOf, note, reason })
  }
  const unit = UNIT_NAME[item.unit] ? t(UNIT_NAME[item.unit] as MessageId) : item.unit
  return (
    <div ref={box} className="cm-edit">
      <FormWindow
        title={t('costModel.edit', { key: item.key })}
        submitLabel={t('costModel.save')}
        busy={save.isPending}
        onSubmit={submit}
        onCancel={onDone}
        alert={save.isError ? <ErrorState error={save.error} /> : null}
      >
        <p className="wide cm-unit">
          {item.custom
            ? t('costModel.unitCustom', {
                unit,
                value: value0({ ...item, value: item.default_value }),
              })
            : t('costModel.unit', { unit })}
        </p>
        <div className="field-d">
          <label className="lbl" htmlFor={ids.value}>
            {ratio ? t('costModel.field.percent') : t('costModel.field.value')}
          </label>
          <input
            id={ids.value}
            className="input mono"
            inputMode="decimal"
            required
            value={value}
            onChange={(e) => setValue(e.target.value)}
          />
        </div>
        <div className="field-d">
          <label className="lbl" htmlFor={ids.basis}>
            {t('costModel.field.basis')}
          </label>
          <select
            id={ids.basis}
            className="select"
            value={basis}
            onChange={(e) => setBasis(e.target.value as CostItem['basis'])}
          >
            {(Object.keys(BASIS) as CostItem['basis'][]).map((b) => (
              <option key={b} value={b}>
                {t(BASIS[b])}
              </option>
            ))}
          </select>
        </div>
        <UtcField
          label={t('costModel.field.asOf')}
          time={false}
          required
          value={asOf}
          onChange={(text) => setAsOf(text)}
        />
        <div className="field-d">
          <label className="lbl" htmlFor={ids.note}>
            {t('costModel.field.note')}
          </label>
          <input
            id={ids.note}
            className="input"
            maxLength={500}
            value={note}
            onChange={(e) => setNote(e.target.value)}
          />
        </div>
        <div className="field-d wide">
          <label className="lbl" htmlFor={ids.source}>
            {t('costModel.field.source')}
          </label>
          <input
            id={ids.source}
            className="input"
            required
            maxLength={500}
            value={source}
            onChange={(e) => setSource(e.target.value)}
          />
        </div>
        <ReasonField value={reason} onChange={setReason} />
        {item.custom ? (
          <div className="wide">
            <button
              type="button"
              className="btn ghost sm"
              disabled={save.isPending || reason.trim() === ''}
              onClick={() => save.mutate({ reset: true, reason })}
            >
              {t('costModel.reset')}
            </button>
          </div>
        ) : null}
      </FormWindow>
    </div>
  )
}

function Items({ data }: { data: CostModel }) {
  const { t, date } = useI18n()
  const value = useValue()
  const [editing, setEditing] = useState<string | null>(null)
  // Each row's Edit button, so closing the window returns focus to it.
  const buttons = useRef(new Map<string, HTMLButtonElement>())
  const close = (key: string) => {
    setEditing(null)
    requestAnimationFrame(() => buttons.current.get(key)?.focus())
  }
  const day = (d: string) => date(`${d}T12:00:00Z`, { timeZone: 'UTC' })
  return (
    <>
      {KINDS.map(({ kind, title }) => {
        const items = data.items.filter((x) => x.kind === kind)
        return (
          <div key={kind}>
            <SectionHead minor title={t(title)} meta={t('costModel.count', { n: items.length })} />
            <DataTable label={t(title)} minWidth={760} className="cm-items">
              {/* One set of widths for the four tables, so they read as one ledger (Mosaic). */}
              <colgroup>
                <col className="cm-c-item" />
                <col className="cm-c-value" />
                <col className="cm-c-basis" />
                <col className="cm-c-source" />
                <col className="cm-c-asof" />
                <col className="cm-c-act" />
              </colgroup>
              <thead>
                <tr>
                  <th className="f" scope="col">
                    {t('costModel.col.item')}
                  </th>
                  <th className="num" scope="col">
                    {t('costModel.col.value')}
                  </th>
                  <th scope="col">{t('costModel.col.basis')}</th>
                  <th scope="col">{t('costModel.col.source')}</th>
                  <th scope="col">{t('costModel.col.asOf')}</th>
                  <th className="last" scope="col">
                    <span className="vh">{t('costModel.col.actions')}</span>
                  </th>
                </tr>
              </thead>
              <tbody>
                {items.map((x) => (
                  <Fragment key={x.key}>
                    <tr>
                      <td className="f wrap">
                        <span translate="no">{x.key}</span>
                        {x.note ? <span className="t2">{x.note}</span> : null}
                      </td>
                      <td className="num">
                        {value(x)}
                        {x.custom ? (
                          <span className="t2">
                            {t('costModel.default', {
                              value: value({ ...x, value: x.default_value }),
                            })}
                          </span>
                        ) : null}
                      </td>
                      <td>{t(BASIS[x.basis])}</td>
                      <td className="src" title={x.source}>
                        {/^https?:\/\//.test(x.source) ? (
                          <a href={x.source} target="_blank" rel="noreferrer noopener">
                            {new URL(x.source).hostname}
                          </a>
                        ) : (
                          x.source
                        )}
                      </td>
                      <td>{day(x.as_of)}</td>
                      <td className="last">
                        <button
                          ref={(el) => {
                            if (el) buttons.current.set(x.key, el)
                            else buttons.current.delete(x.key)
                          }}
                          type="button"
                          className="btn ghost sm"
                          aria-expanded={editing === x.key}
                          aria-label={t('costModel.edit', { key: x.key })}
                          onClick={() => setEditing(editing === x.key ? null : x.key)}
                        >
                          {t('costModel.editShort')}
                        </button>
                      </td>
                    </tr>
                    {editing === x.key ? (
                      <tr className="edit money-edit">
                        <td colSpan={6}>
                          <Edit item={x} onDone={() => close(x.key)} />
                        </td>
                      </tr>
                    ) : null}
                  </Fragment>
                ))}
              </tbody>
            </DataTable>
          </div>
        )
      })}
    </>
  )
}

export function CostModelPage() {
  const { t } = useI18n()
  const model = useQuery(costModelQuery)
  const v = model.data
  return (
    <main id="main" className="pg">
      <PageHead
        title={t('costModel.title')}
        meta={
          v
            ? t('costModel.meta', {
                version: v.version,
                custom: v.items.filter((x) => x.custom).length,
                total: v.items.length,
              })
            : undefined
        }
      />
      {model.isPending ? (
        <Loading />
      ) : model.isError ? (
        <ErrorState error={model.error} retry={() => model.refetch()} />
      ) : (
        <Model v={model.data} />
      )}
    </main>
  )
}

const PROFILES = [
  ['no_replay', 'costModel.profile.noReplay'],
  ['typical', 'costModel.profile.typical'],
  ['heavy', 'costModel.profile.heavy'],
] as const

const UNITS: [keyof CostModel['units'], MessageId][] = [
  ['summary_per_1k', 'costModel.unit.summary'],
  ['crash_per_1k', 'costModel.unit.crash'],
  ['replay_minute_per_1k', 'costModel.unit.replayMinute'],
  ['record_per_million', 'costModel.unit.record'],
  ['span_per_million', 'costModel.unit.span'],
  ['point_per_million', 'costModel.unit.point'],
  ['otlp_per_gb', 'costModel.unit.otlp'],
  ['symbols_per_gb_month', 'costModel.unit.symbols'],
]

/** The model: the strip, the price per thousand by profile, the unbilled units, every item. */
function Model({ v }: { v: CostModel }) {
  const { t } = useI18n()
  const { usd } = useFormat()
  return (
    <>
      <Strip
        items={[
          {
            dt: t('costModel.fixed'),
            dd: usd(v.fixed_monthly_usd, 0),
            d: t('costModel.fixedNote'),
          },
          ...PROFILES.map(([p, label]) => ({
            dt: t(label),
            dd: usd(v.per_thousand[p].total, 3),
            d: t('costModel.per1k'),
          })),
        ]}
      />
      <div className="grid2">
        <Section title={t('costModel.prices')} meta={t('costModel.pricesMeta')}>
          <DataTable label={t('table.of', { name: t('costModel.prices') })} minWidth={520}>
            <thead>
              <tr>
                <th className="f" scope="col">
                  {t('costModel.col.component')}
                </th>
                {PROFILES.map(([p, label]) => (
                  <th key={p} className={p === 'heavy' ? 'num last' : 'num'} scope="col">
                    {t(label)}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {COMPONENTS.map((c) => (
                <tr key={c}>
                  <td className="f">{t(`costModel.component.${c}`)}</td>
                  {PROFILES.map(([p]) => (
                    <td key={p} className={p === 'heavy' ? 'num last' : 'num'}>
                      {usd(v.per_thousand[p][c], 4)}
                    </td>
                  ))}
                </tr>
              ))}
              <tr className="total">
                <td className="f">{t('costModel.component.total')}</td>
                {PROFILES.map(([p]) => (
                  <td key={p} className={p === 'heavy' ? 'num last' : 'num'}>
                    {usd(v.per_thousand[p].total, 4)}
                  </td>
                ))}
              </tr>
            </tbody>
          </DataTable>
        </Section>
        <Section title={t('costModel.unbilled')} meta={t('costModel.unbilledMeta')}>
          <KV
            label={t('costModel.unbilled')}
            items={UNITS.map(([k, label]) => [t(label), usd(v.units[k], 4)] as const)}
          />
        </Section>
      </div>
      <Section title={t('costModel.items')} meta={t('costModel.itemsMeta')}>
        <Items data={v} />
      </Section>
    </>
  )
}
